/**
 * /admin — onglet « Garages » (2026-09-17).
 *
 * L'écran d'une décision, pas d'une formalité. Un garage inscrit a déjà passé
 * la vérification du SIRET auprès de l'annuaire : c'est automatique, et ça ne
 * prouve qu'une chose, que la société existe. Ce que VOUS décidez ici, c'est
 * s'il voit des vendeurs. Les deux conditions sont donc affichées séparément,
 * et le bouton de validation reste fermé tant que le SIRET n'est pas vérifié.
 *
 * Les crédits sont accordés à la main tant que le paiement n'est pas en ligne.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import './AdminGarages.css';

const FILTRES = [
  ['en_attente', 'En attente'],
  ['valides', 'Validés'],
  ['refuses', 'Refusés'],
];

const quand = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const siretLisible = (s) => (s || '').replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4');

const message = (err, repli) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return repli;
};

function Garage({ garage, creditsBienvenue, onValider, onRefuser, onCrediter, occupe }) {
  const [refus, setRefus] = useState(false);
  const [motif, setMotif] = useState('');
  const [credits, setCredits] = useState(creditsBienvenue);
  const [appoint, setAppoint] = useState(5);

  const enAttente = !garage.valide_par_admin && garage.is_active;

  return (
    <article className="zk-adg-garage zk-carte">
      <header>
        <h3>{garage.raison_sociale || garage.username}</h3>
        <span className={`zk-adg-statut ${garage.is_active ? (garage.valide_par_admin ? 'valide' : 'attente') : 'refuse'}`}>
          {garage.is_active ? (garage.valide_par_admin ? 'Validé' : 'En attente') : 'Refusé'}
        </span>
      </header>

      <p className="zk-adg-adresse">{garage.adresse || 'Adresse inconnue'}</p>

      {/* Les deux barrières, montrées comme deux barrières. */}
      <ul className="zk-adg-conditions">
        <li className={garage.siret_verifie ? 'ok' : 'ko'}>
          SIRET {siretLisible(garage.siret)} — {garage.siret_verifie ? 'vérifié à l’annuaire' : 'NON vérifié'}
        </li>
        <li className={garage.valide_par_admin ? 'ok' : 'ko'}>
          Accès aux dépôts — {garage.valide_par_admin ? 'ouvert par vous' : 'fermé'}
        </li>
      </ul>

      <dl className="zk-adg-chiffres">
        <div><dt>Rayon</dt><dd>{garage.rayon_km} km</dd></div>
        <div><dt>Crédits</dt><dd>{garage.credits_restants ?? 0}</dd></div>
        <div><dt>Position</dt><dd>{garage.lat != null ? 'connue' : 'inconnue'}</dd></div>
      </dl>

      <p className="zk-adg-contact">
        <a href={`mailto:${garage.email}`}>{garage.email}</a>
        {garage.telephone ? <> · <a href={`tel:${garage.telephone}`}>{garage.telephone}</a></> : null}
      </p>
      <p className="zk-adg-date">Demande du {quand(garage.created_at)}</p>

      {!garage.siret_verifie && (
        <p className="zk-adg-blocage">
          Le SIRET n’a pas été vérifié : l’accès ne peut pas être ouvert.
        </p>
      )}

      {enAttente && garage.siret_verifie && !refus && (
        <div className="zk-adg-validation">
          <label htmlFor={`cred-${garage.id}`}>Crédits à l’ouverture</label>
          <input
            id={`cred-${garage.id}`} type="number" min="0" max="1000" inputMode="numeric"
            value={credits} onChange={(e) => setCredits(e.target.value)}
          />
          <button type="button" className="zk-bouton" disabled={occupe}
                  onClick={() => onValider(garage, Number(credits) || 0)}>
            Ouvrir l’accès
          </button>
          <button type="button" className="zk-bouton second" disabled={occupe} onClick={() => setRefus(true)}>
            Refuser
          </button>
        </div>
      )}

      {garage.valide_par_admin && !refus && (
        <div className="zk-adg-validation">
          <label htmlFor={`app-${garage.id}`}>Ajouter des crédits</label>
          <input
            id={`app-${garage.id}`} type="number" min="0" max="1000" inputMode="numeric"
            value={appoint} onChange={(e) => setAppoint(e.target.value)}
          />
          <button type="button" className="zk-bouton" disabled={occupe}
                  onClick={() => onCrediter(garage, Number(appoint) || 0)}>
            Créditer
          </button>
          <button type="button" className="zk-bouton second" disabled={occupe} onClick={() => setRefus(true)}>
            Fermer l’accès
          </button>
        </div>
      )}

      {refus && (
        <div className="zk-adg-refus">
          <label htmlFor={`motif-${garage.id}`}>Motif (envoyé au garage)</label>
          <input
            id={`motif-${garage.id}`} value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="Hors zone de couverture, activité sans rapport…"
          />
          <div className="zk-adg-actions">
            <button type="button" className="zk-bouton" disabled={occupe || motif.trim().length < 3}
                    onClick={() => onRefuser(garage, motif.trim())}>
              Confirmer le refus
            </button>
            <button type="button" className="zk-bouton second" disabled={occupe}
                    onClick={() => { setRefus(false); setMotif(''); }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function AdminGarages({ signaler }) {
  const [filtre, setFiltre] = useState('en_attente');
  const [garages, setGarages] = useState([]);
  const [comptes, setComptes] = useState({});
  const [creditsBienvenue, setCreditsBienvenue] = useState(3);
  const [chargement, setChargement] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get('/api/admin/garages', { params: { statut: filtre } });
      setGarages(data.garages || []);
      setComptes(data.comptes || {});
      if (data.credits_bienvenue != null) setCreditsBienvenue(data.credits_bienvenue);
    } catch (err) {
      signaler?.(message(err, 'Impossible de charger les garages.'), false);
    } finally {
      setChargement(false);
    }
  }, [filtre, signaler]);

  useEffect(() => { charger(); }, [charger]);

  async function agir(action, garage, corps, succes) {
    setOccupe(true);
    try {
      await api.post(`/api/admin/garages/${garage.id}/${action}`, corps);
      signaler?.(succes);
      await charger();
    } catch (err) {
      signaler?.(message(err, 'L’action a échoué.'), false);
    } finally {
      setOccupe(false);
    }
  }

  const nom = (g) => g.raison_sociale || g.email;

  return (
    <section className="zk-adg">
      <div className="zk-adg-tete">
        <div className="zk-adm-onglets" role="tablist" aria-label="Statut des garages">
          {FILTRES.map(([id, libelle]) => (
            <button
              key={id} type="button" role="tab" aria-selected={filtre === id}
              className={`zk-aff-pilule${filtre === id ? ' on' : ''}`} onClick={() => setFiltre(id)}
            >
              {libelle}{comptes[id] != null ? ` (${comptes[id]})` : ''}
            </button>
          ))}
        </div>
        <button type="button" className="zk-bouton second" onClick={charger} disabled={chargement}>
          Rafraîchir
        </button>
      </div>

      {chargement && garages.length === 0 && <p className="zk-adg-vide">Chargement…</p>}
      {!chargement && garages.length === 0 && (
        <p className="zk-adg-vide">
          {filtre === 'en_attente' ? 'Aucune demande en attente.' : 'Aucun garage dans cette liste.'}
        </p>
      )}

      <div className="zk-adg-liste">
        {garages.map((g) => (
          <Garage
            key={g.id} garage={g} creditsBienvenue={creditsBienvenue} occupe={occupe}
            onValider={(garage, credits) => agir('valider', garage, { credits },
              `Accès ouvert à ${nom(garage)} avec ${credits} crédit${credits > 1 ? 's' : ''}.`)}
            onRefuser={(garage, motif) => agir('refuser', garage, { motif },
              `Accès fermé pour ${nom(garage)}.`)}
            onCrediter={(garage, credits) => agir('credits', garage, { credits },
              `${credits} crédit${credits > 1 ? 's' : ''} ajouté${credits > 1 ? 's' : ''} à ${nom(garage)}.`)}
          />
        ))}
      </div>
    </section>
  );
}
